import { scopedMatch } from "./collectionScope";

const keyFor = (value) => {
  if (Array.isArray(value)) return `[${value.map(keyFor).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${keyFor(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
};

const copy = (value) => value === undefined ? undefined : JSON.parse(JSON.stringify(value));
const field = (payload, name) => payload?.[name] === undefined ? payload?.data?.[name] : payload[name];
const unscoped = (payload) => {
  const next = { ...payload };
  delete next.scope;
  if (next.data && typeof next.data === "object" && !Array.isArray(next.data)) next.data = unscoped(next.data);
  return next;
};
const selectedResponse = (response, payload) => {
  const scope = field(payload, "scope");
  const result = response?.data;
  const ids = result?.page?.documentIds;
  // Old backends return a full legacy collection without snapshot ID metadata.
  // Keep its original DTO and let existing screen filters handle that response.
  if (!scope || result?.success !== true || !Array.isArray(result.data) || !Array.isArray(ids)) return response;
  const selected = result.data.map((record, index) => ({ record, id: ids[index] }))
    .filter(({ record, id }) => scopedMatch(field(payload, "systemName"), id, record, scope));
  return { ...response, data: { ...result, data: selected.map(({ record }) => record),
    page: { ...result.page, documentIds: selected.map(({ id }) => id) } } };
};

export function createCollectionRequests() {
  const inFlight = new Map();
  let revision = 0;
  const invalidate = () => {
    revision += 1;
    inFlight.clear();
  };

  const collect = async (call, payload, getIdentity, ownerIdentity, startRevision, retry = true) => {
    const records = [];
    const documentIds = [];
    let hasDocumentIds = true;
    let cursor = null;
    let readTime = null;
    let pageSize = String(field(payload, "systemName") || "").trim().toLowerCase() === "learnerassessmentstory" ? 16 : 250;
    let response;
    const cursors = new Set();
    do {
      if (getIdentity() !== ownerIdentity) throw new Error("The signed-in user changed during collection loading.");
      response = await call({ ...payload, page: { size: pageSize, ...(cursor ? { after: cursor } : {}),
        ...(readTime ? { readTime } : {}) } });
      const result = response?.data;
      if (result?.success !== true || !Array.isArray(result.data)) {
        if (cursor) throw new Error("A later collection page was unsuccessful; the results are incomplete.");
        return response;
      }
      records.push(...result.data);
      if (Array.isArray(result.page?.documentIds) && result.page.documentIds.length === result.data.length) {
        documentIds.push(...result.page.documentIds);
      } else hasDocumentIds = false;
      const currentReadTime = result.page?.readTime;
      if (readTime && currentReadTime && keyFor(readTime) !== keyFor(currentReadTime)) {
        throw new Error("Collection pages used inconsistent snapshots.");
      }
      if (!readTime && currentReadTime) readTime = currentReadTime;
      const suggestion = result.page?.suggestedSize;
      if (Number.isInteger(suggestion) && suggestion > 0 && suggestion <= pageSize) pageSize = suggestion;
      const next = result.page?.nextCursor;
      if (result.page?.complete === false && !next) throw new Error("Collection pagination did not provide a continuation cursor.");
      if (!next || result.page?.complete === true) cursor = null;
      else {
        if (typeof next !== "string" || cursors.has(next)) throw new Error("Collection pagination did not advance.");
        cursors.add(next);
        cursor = next;
      }
    } while (cursor);
    if (getIdentity() !== ownerIdentity) throw new Error("The signed-in user changed during collection loading.");
    // A write completed while earlier pages were loading. Start again so a
    // freshly saved assessment cannot be hidden behind an older in-flight load.
    if (revision !== startRevision) {
      if (retry) return collect(call, payload, getIdentity, ownerIdentity, revision, false);
      throw new Error("Records changed repeatedly during collection loading. Please refresh the results.");
    }
    return { ...response, data: { ...response.data, data: records, page: { complete: true, nextCursor: null,
      ...(hasDocumentIds ? { documentIds } : {}), ...(readTime ? { readTime } : {}) } } };
  };

  const load = (call, payload, { identity, getIdentity = () => identity, clientId = "default", requestOptions } = {}) => {
    // Explicit page consumers retain their page-by-page contract.
    if (field(payload, "page")) return call(payload);
    const ownerIdentity = getIdentity();
    const request = unscoped(payload);
    const target = Object.fromEntries(["company", "collectionBrand", "systemName", "documentIds"]
      .map((name) => [name, field(request, name)]));
    const key = `${clientId}:${String(ownerIdentity)}:${revision}:${keyFor(target)}:${keyFor(requestOptions)}`;
    let pending = inFlight.get(key);
    if (!pending) {
      // Parent/child screens often select different learners from the same
      // collection. Read one complete snapshot, then select copied responses.
      pending = collect(call, request, getIdentity, ownerIdentity, revision);
      inFlight.set(key, pending);
      const clear = () => { if (inFlight.get(key) === pending) inFlight.delete(key); };
      pending.then(clear, clear);
    }
    // Sharing stops when loading finishes. Grades and Funder reports always
    // fetch fresh data on later navigation; each consumer owns its response.
    return pending.then((response) => copy(selectedResponse(response, payload)));
  };

  return { load, invalidate };
}
